/**
 * /ask 답변·인용 렌더 공유(Ask 페이지 + Ask 기록 상세). 두 곳이 동일하게 보이도록 단일 정본.
 */
import { Link } from 'react-router-dom';
import ReactMarkdown, { type Components } from 'react-markdown';
import remarkGfm from 'remark-gfm';

// /ask wiki context 섹션(ask.ts WIKI_CONTEXT_SECTIONS) → 사용자 라벨.
export const WIKI_LABEL: Record<string, string> = {
  tldr: '한눈 요약',
  thesis: '투자 논거',
  catalysts: '촉매',
  broker_views: '증권사 의견',
};

// 필드 대부분 optional — history 상세의 redacted/legacy/partial row 도 크래시 없이 렌더(codex P2).
export type Citation = {
  chunk_id: string;
  report_id?: string;
  report_title?: string;
  broker?: string | null;
  published_at?: string;
  page_number?: number | null;
  similarity?: number;
  snippet?: string;
};

const SUPERSCRIPT = '⁰¹²³⁴⁵⁶⁷⁸⁹';
const toSup = (n: number) =>
  String(n)
    .split('')
    .map((d) => SUPERSCRIPT[Number(d)])
    .join('');

// 답변 본문 `[chunk_id]`(또는 `[id1, id2]`) 인라인 마커 → 각주 위첨자 링크 `[ⁿ](#cite-id)`.
// 번호 = chunks_used 인덱스(인용 카드 [n] 과 일치). chunks_used 에 없는 마커는 제거.
export function linkifyCitations(md: string, chunks: { chunk_id: string }[]): string {
  const numById = new Map(chunks.map((c, i) => [c.chunk_id, i + 1]));
  const idPattern = '[a-f0-9]{8,}(?::(?:text|vision))?:\\d+';
  const re = new RegExp(`\\[((?:${idPattern})(?:,\\s*(?:${idPattern}))*)\\]`, 'g');
  return md.replace(re, (_, raw: string) =>
    raw
      .split(/,\s*/)
      .map((id) => {
        const n = numById.get(id);
        return n ? `[${toSup(n)}](#cite-${id})` : '';
      })
      .join(''),
  );
}

// #cite- 링크는 각주 위첨자(인용 카드로 점프), 그 외는 새 탭.
const answerComponents: Components = {
  a({ href, children, ...rest }) {
    if (href && href.startsWith('#cite-')) {
      return (
        <a href={href} className="font-medium text-blue-600 no-underline hover:underline">
          {children}
        </a>
      );
    }
    return (
      <a href={href} target="_blank" rel="noreferrer" {...rest}>
        {children}
      </a>
    );
  },
};

/** 답변 markdown(각주 위첨자 linkify). */
export function AnswerMarkdown({ answer, chunks }: { answer: string; chunks: { chunk_id: string }[] }) {
  return (
    <div className="prose prose-slate prose-sm mt-3 max-w-none leading-relaxed prose-p:text-slate-800 prose-strong:text-slate-900 prose-li:my-0.5 prose-ul:my-2 prose-ol:my-2">
      <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]} components={answerComponents}>
        {linkifyCitations(answer, chunks)}
      </ReactMarkdown>
    </div>
  );
}

/** 근거 위키 섹션 배지. */
export function WikiBadges({ sections }: { sections?: string[] }) {
  if (!sections || sections.length === 0) return null;
  return (
    <div className="mt-3 flex flex-wrap items-center gap-1.5 border-t border-slate-100 pt-3 text-xs text-slate-500">
      <span className="font-medium">근거 위키 섹션:</span>
      {sections.map((s) => (
        <span key={s} className="rounded bg-slate-100 px-2 py-0.5 font-medium text-slate-700">
          {WIKI_LABEL[s] ?? s}
        </span>
      ))}
    </div>
  );
}

/** 리포트 인용 카드 목록. redacted=스니펫 비표시(reports:text/ask:write 없음). */
export function CitationCards({
  chunks,
  wikiUsed,
  redacted,
}: {
  chunks: Citation[];
  wikiUsed?: string[];
  redacted?: boolean;
}) {
  return (
    <div>
      <h2 className="mb-2 text-sm font-semibold text-slate-700">
        리포트 인용 ({chunks.length})
        {redacted && <span className="ml-2 text-xs font-normal text-slate-400">· 스니펫 비표시 (reports:text 필요)</span>}
      </h2>
      {chunks.length === 0 ? (
        wikiUsed && wikiUsed.length > 0 ? (
          <p className="text-xs text-slate-400">리포트 청크 직접 인용 없음 — 위 위키 근거로 답변.</p>
        ) : (
          <p className="text-xs text-slate-400">근거 부족 — 관련 리포트·위키를 찾지 못함.</p>
        )
      ) : (
        <ul className="space-y-2">
          {chunks.map((c, i) => (
            <li
              key={i}
              id={`cite-${c.chunk_id}`}
              className="scroll-mt-20 rounded-md border border-slate-200 bg-white px-4 py-3 text-sm"
            >
              <div className="flex items-center gap-2 text-xs text-slate-500">
                <span className="rounded bg-slate-100 px-1.5 font-semibold text-slate-600">{i + 1}</span>
                {c.broker && <span>{c.broker}</span>}
                {c.published_at && <span>· {c.published_at}</span>}
                {c.page_number != null && <span>· p.{c.page_number}</span>}
                {c.similarity != null && <span>· sim {c.similarity.toFixed(3)}</span>}
              </div>
              {c.report_id ? (
                <Link to={`/reports/${c.report_id}/md`} className="mt-1 block font-medium text-blue-700 hover:underline">
                  {c.report_title ?? c.report_id}
                </Link>
              ) : (
                <p className="mt-1 font-medium">{c.report_title ?? c.chunk_id}</p>
              )}
              {c.snippet && (
                <div className="prose prose-slate prose-sm mt-2 max-h-64 max-w-none overflow-y-auto text-slate-600 prose-p:my-1 prose-table:my-1 prose-table:text-xs prose-thead:bg-slate-50 prose-th:px-1.5 prose-th:py-0.5 prose-td:px-1.5 prose-td:py-0.5 prose-li:my-0">
                  <ReactMarkdown remarkPlugins={[[remarkGfm, { singleTilde: false }]]}>{c.snippet}</ReactMarkdown>
                </div>
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
